/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link, Tag, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import AvailabilityDetails from 'bundles/unified-description-page-common/components/modules/syllabus/AvailabilityDetails';
import LearningObjective from 'bundles/unified-description-page-common/components/shared/LearningObjective';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import NewAiBadge from 'bundles/unified-description-page-common/components/shared/badges/NewAiBadge';
import type { FixedSessionFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedEnterpriseEntitiesFragments';
import { getGoogleRefreshAiBadgeContent } from 'bundles/unified-description-page-common/utils/getGoogleRefreshAiBadgeContent';
import { shouldShowGoogleRefreshXdpAiSyllabus } from 'bundles/unified-description-page-common/utils/shouldShowGoogleRefresh';

import _t from 'i18n!nls/xdp';

type ObjectiveType = React.ComponentProps<typeof LearningObjective>['objective'];
type DescriptionType = React.ComponentProps<typeof TogglableDescription>['cmlDescription'];

export type CourseItemContentElement = React.ReactElement<Props, typeof CourseItemContent>;

interface Props {
  learningObjectives?: ObjectiveType[] | null;
  description?: string | null;
  cmlDescription?: DescriptionType | null;
  skills?: string[] | null;
  fixedSession?: FixedSessionFragment | null;
  s12nSlug?: string;
  courseSlug?: string;
  link?: string;
}

const styles = {
  skills: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-100);
  `,
  learningObjective: css`
    & + & {
      padding-top: var(--cds-spacing-100);
    }
  `,
  aiContent: css`
    margin-top: calc(var(--cds-spacing-50) * -1);

    ul {
      padding-left: 1.5em;

      li {
        list-style: disc;
      }
    }
  `,
  title: css`
    padding-bottom: var(--cds-spacing-100) !important;
  `,
  viewCourseLink: css`
    color: var(--cds-color-neutral-primary) !important;
  `,
};

const CourseItemContent = ({
  learningObjectives,
  description,
  skills,
  cmlDescription,
  fixedSession,
  s12nSlug,
  courseSlug,
  link,
}: Props) => {
  const hasLearningObjectives = learningObjectives && learningObjectives?.length > 0;
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  return (
    <div>
      {fixedSession && <AvailabilityDetails fixedSession={fixedSession} />}
      {(hasLearningObjectives || description) && (
        <Subsection
          title={
            <Typography2 variant="actionSecondary" component="h4">
              {_t("What you'll learn")}
            </Typography2>
          }
        >
          {hasLearningObjectives ? (
            <ul>
              {learningObjectives.map((objective, index) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={index} css={styles.learningObjective}>
                    <LearningObjective objective={objective} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <TogglableDescription description={description} cmlDescription={cmlDescription} />
          )}
        </Subsection>
      )}
      {shouldShowGoogleRefreshXdpAiSyllabus(s12nSlug, courseSlug) && (
        <Subsection title={<NewAiBadge slug={s12nSlug} disabled />}>
          <Typography2 variant="bodyPrimary" component="div" css={styles.aiContent}>
            {getGoogleRefreshAiBadgeContent(s12nSlug)}
          </Typography2>
        </Subsection>
      )}
      {skills && skills.length > 0 && (
        <Subsection
          title={
            <Typography2 variant="actionSecondary" component="h4">
              {_t("Skills you'll gain")}
            </Typography2>
          }
        >
          <div css={styles.skills}>
            {skills.map((skill, index) => {
              return (
                <Tag
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                >
                  {skill}
                </Tag>
              );
            })}
          </div>
        </Subsection>
      )}
      {/* TODO: Tools will be added here at a later date. */}
      {isMobile && (
        <Subsection>
          <Typography2 variant="actionPrimary" component="span">
            <Link href={link} css={styles.viewCourseLink}>
              {
                // eslint-disable-next-line no-restricted-syntax
                _t('View course')
              }
            </Link>
          </Typography2>
        </Subsection>
      )}
    </div>
  );
};

export default CourseItemContent;
